import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, Sun, Moon, Heart, MessageCircle, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { NavSearch } from "@/components/NavSearch";
import timbernetLogoLight from "@/assets/timbernet-logo-light.webp";
import timbernetLogoDark from "@/assets/timbernet-logo-dark.webp";

const navLinks = [
  { to: "/", label: "Hem" },
  { to: "/annonser", label: "Annonser" },
  { to: "/profiler", label: "Profiler" },
  { to: "/lar-dig-mer", label: "Lär dig mer" },
  { to: "/skogsverktyg", label: "Verktyg" },
];

function initialsOf(name?: string | null): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

export function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const { theme } = useTheme();
  const logoSrc = theme === "dark" ? timbernetLogoDark : timbernetLogoLight;

  const goToChats = () => {
    try {
      sessionStorage.setItem("dashboard-active-tab-v1", "messages");
    } catch {
      // ignore
    }
    navigate(user ? "/dashboard" : "/logga-in");
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-card/80 backdrop-blur-lg">
      <div className="container flex h-20 items-center gap-4 md:h-24">
        <Link to="/" className="flex shrink-0 items-center">
          <img src={logoSrc} alt="TimberNet logotyp" className="h-16 w-auto md:h-20" />
        </Link>

        {/* Desktop search */}
        <div className="hidden flex-1 justify-start md:flex">
          <NavSearch />
        </div>

        {/* Desktop nav */}
        <nav className="hidden shrink-0 items-center gap-1 md:flex">
          <Link
            to="/annonser"
            state={{ fresh: true }}
            className={`rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-secondary ${
              location.pathname === "/annonser" ? "text-primary" : "text-foreground"
            }`}
          >
            Annonser
          </Link>
          <Link
            to="/lar-dig-mer"
            className={`rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-secondary ${
              location.pathname.startsWith("/lar-dig-mer") ? "text-primary" : "text-foreground"
            }`}
          >
            Lär dig mer
          </Link>

          <Button
            variant="ghost"
            size="icon"
            className="rounded-full text-muted-foreground"
            aria-label="Favoriter (kommer snart)"
            disabled
          >
            <Heart className="h-5 w-5" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="rounded-full"
            aria-label="Meddelanden"
            onClick={goToChats}
          >
            <MessageCircle className="h-5 w-5" />
          </Button>

          <Button size="sm" className="rounded-full px-4" asChild>
            <Link to={user ? "/skapa-annons" : "/logga-in"}>
              <Plus className="mr-1.5 h-4 w-4" /> Sälj
            </Link>
          </Button>

          {user ? (
            <Link
              to="/dashboard"
              aria-label="Min dashboard"
              className="ml-1 flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-secondary text-sm font-semibold text-foreground transition-colors hover:bg-secondary/80"
            >
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt={profile?.full_name || "Profil"}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span>{initialsOf(profile?.full_name)}</span>
              )}
            </Link>
          ) : (
            <Button variant="outline" size="sm" className="ml-1" asChild>
              <Link to="/logga-in">Logga in</Link>
            </Button>
          )}
        </nav>

        {/* Mobile toggle */}
        <button className="ml-auto md:hidden" onClick={() => setOpen(!open)} aria-label="Meny">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>


      {/* Mobile menu overlay */}
      {open && (
        <div
          className="md:hidden fixed left-0 right-0 top-20 border-t border-border bg-card shadow-lg"
          style={{ zIndex: 1000 }}
        >
        <nav className="container flex flex-col gap-1 py-4">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              state={l.to === "/annonser" ? { fresh: true } : undefined}
              onClick={() => setOpen(false)}
              className={`rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                location.pathname === l.to ? "bg-secondary text-primary" : "text-muted-foreground"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <div className="mt-2 flex gap-2">
            {user ? (
              <>
                <Button variant="outline" size="sm" className="flex-1" asChild>
                  <Link to="/dashboard" onClick={() => setOpen(false)}>Dashboard</Link>
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { signOut(); setOpen(false); }} aria-label="Logga ut">
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" className="flex-1" asChild>
                  <Link to="/logga-in" onClick={() => setOpen(false)}>Logga in</Link>
                </Button>
                <Button size="sm" className="flex-1" asChild>
                  <Link to="/registrera" onClick={() => setOpen(false)}>Registrera</Link>
                </Button>
              </>
            )}
          </div>
        </nav>
      </div>
      )}

      {/* Backdrop for closing on outside tap */}
      {open && (
        <div
          className="md:hidden fixed inset-0 top-20"
          style={{ zIndex: 999 }}
          onClick={() => setOpen(false)}
        />
      )}
    </header>
  );
}

export function Footer({ hideOnMobile = false }: { hideOnMobile?: boolean } = {}) {
  const { theme, toggleTheme } = useTheme();
  const logoSrc = theme === "dark" ? timbernetLogoDark : timbernetLogoLight;

  return (
    <footer className={`border-t border-border bg-card${hideOnMobile ? " hidden md:block" : ""}`}>
      <div className="container py-12">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="mb-3">
              <img src={logoSrc} alt="TimberNet logotyp" className="h-16 w-auto" />
            </div>
            <p className="text-sm text-muted-foreground">
              Sveriges digitala marknadsplats för skogstjänster och virke. Enkelt, tryggt och lokalt.
            </p>
          </div>
          <div>
            <h4 className="mb-3 font-sans text-sm font-semibold">Tjänster</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/skogstjanster" className="hover:text-primary transition-colors">Boka tjänst</Link></li>
              <li><Link to="/annonser" state={{ fresh: true }} className="hover:text-primary transition-colors">Köp ved & virke</Link></li>
              <li><Link to="/skogsverktyg" className="hover:text-primary transition-colors">Digitala verktyg</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="mb-3 font-sans text-sm font-semibold">Information</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/om-oss" className="hover:text-primary transition-colors">Om TimberNet</Link></li>
              <li><Link to="/villkor" className="hover:text-primary transition-colors">Villkor</Link></li>
              <li><Link to="/integritetspolicy" className="hover:text-primary transition-colors">Integritetspolicy</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="mb-3 font-sans text-sm font-semibold">Kontakt</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>info@timbernet.se</li>
              
            </ul>
          </div>
        </div>
        <div className="mt-8 flex items-center justify-between border-t border-border pt-6">
          <span className="text-xs text-muted-foreground">
            © 2026 TimberNet. Alla rättigheter förbehållna.
          </span>
          <button
            onClick={toggleTheme}
            className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            aria-label={theme === "dark" ? "Byt till ljust läge" : "Byt till mörkt läge"}
          >
            {theme === "dark" ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
            <span className="hidden sm:inline">{theme === "dark" ? "Ljust läge" : "Mörkt läge"}</span>
          </button>
        </div>
      </div>
    </footer>
  );
}